/**
 * By-Person grouping — pure, deterministic roll-up powering the Sprint Board's
 * "By Person" view.
 *
 * Groups the active sprint's TOP-LEVEL tasks by owner alias into per-person
 * lanes, each carrying the person's roster identity (name + role), their
 * assigned effort vs individual capacity (via the {@link ../engine/capacity}
 * engine over the sprint's working days), total story points, and their
 * TODO / IN_PROGRESS / DONE / blocked counts. Tasks with no owner fall into a
 * trailing "Unassigned" lane. Member lanes are ordered by assigned effort desc.
 *
 * Pure TS — no React, no I/O. Task inputs are the extended {@link Task}; roster
 * inputs are {@link TeamMember} so identity (name/role) resolves from the team.
 */

import type { Task } from '../../tasks/types';
import type { TeamMember } from '../../teams/types';
import {
  engineerLoads,
  memberBandwidthDays,
  type CapacityMember,
} from '../engine/capacity';
import { isSubtask, totalEffortDays } from '../../tasks/taskModel';
import { memberOncallDays } from './sprintboardModel';

/** TODO / IN_PROGRESS / DONE / blocked counts for a person's tasks. */
export interface PersonCounts {
  todo: number;
  inProgress: number;
  done: number;
  blocked: number;
}

/** A single person's lane in the By-Person view. */
export interface PersonLane {
  /** Owner alias, or '' for the Unassigned lane. */
  alias: string;
  /** Display name (roster) — falls back to the alias. */
  name: string;
  /** Role from the roster — empty when unknown / unassigned. */
  role: string;
  /** True for the trailing Unassigned lane. */
  isUnassigned: boolean;
  /** The person's top-level tasks (input order preserved). */
  tasks: Task[];
  /** Sum of the tasks' effortDays (ideal days). */
  assignedDays: number;
  /** The person's individual capacity for the sprint (sprint working days). */
  capacityDays: number;
  /** capacity − assigned (may be negative). */
  remainingDays: number;
  /** True when assigned exceeds capacity. */
  overCapacity: boolean;
  /** Sum of the tasks' story points. */
  storyPoints: number;
  /** Board-lane + blocked counts. */
  counts: PersonCounts;
}

/** Rounds to 2 decimals to keep half-day arithmetic clean. */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Sums story points across tasks (missing → 0). */
function sumStoryPoints(tasks: Task[]): number {
  return tasks.reduce(
    (sum, t) => sum + (typeof t.storyPoints === 'number' ? t.storyPoints : 0),
    0,
  );
}

/** Tallies board-lane + blocked counts for a person's tasks. */
function tally(tasks: Task[]): PersonCounts {
  const counts: PersonCounts = { todo: 0, inProgress: 0, done: 0, blocked: 0 };
  for (const t of tasks) {
    const lane = t.boardStatus ?? 'TODO';
    if (lane === 'DONE') counts.done += 1;
    else if (lane === 'IN_PROGRESS') counts.inProgress += 1;
    else counts.todo += 1;
    if (t.blocked) counts.blocked += 1;
  }
  return counts;
}

/**
 * Optional bandwidth inputs for {@link buildPersonLanes}. All optional so legacy
 * callers keep working (no on-call, no common tasks, no subtasks).
 */
export interface PersonLaneOptions {
  /** Manually-entered on-call days per member alias for the sprint. */
  oncallDaysByAlias?: Record<string, number>;
  /** Total per-member bandwidth reduction from the sprint's common tasks. */
  commonReductionDays?: number;
  /**
   * Subtasks keyed by parent task id, so a top-level task that was never
   * estimated directly still counts its subtasks' effort as its occupied load.
   */
  subtasksByParent?: Map<string, Task[]>;
}

/**
 * Builds the ordered By-Person lanes for a sprint.
 *
 * Only TOP-LEVEL tasks are grouped (subtasks are excluded from the lanes). A
 * lane is created for every owner alias that has ≥1 task — whether or not they
 * are on the roster — plus a trailing "Unassigned" lane for tasks with no owner.
 *
 * Per-person capacity is each member's BANDWIDTH (see {@link memberBandwidthDays}
 * / {@link engineerLoads}) — sprint working days minus their on-call days, ×1.08,
 * minus the common-task reduction — not a flat working-days figure. Occupied
 * (`assignedDays`) is the sum of the owned tasks' TOTAL effort (own custom effort
 * or, failing that, their subtasks' effort); leftover is `remainingDays`
 * (capacity − occupied, may be negative). Owners absent from the roster still get
 * a lane, with capacity = a member's bandwidth for the sprint. Lanes are sorted
 * by occupied effort desc (name as a tiebreak); Unassigned is always last.
 *
 * @param tasks             the sprint's tasks (subtasks are filtered out of lanes)
 * @param members           the linked team's roster (for name/role + capacity)
 * @param sprintWorkingDays working days (Mon–Fri) in the sprint
 * @param options           optional bandwidth inputs (on-call, common tasks, subtasks)
 */
export function buildPersonLanes(
  tasks: Task[],
  members: TeamMember[],
  sprintWorkingDays: number,
  options: PersonLaneOptions = {},
): PersonLane[] {
  const { oncallDaysByAlias, commonReductionDays = 0, subtasksByParent } = options;
  const topLevel = (tasks ?? []).filter((t) => !isSubtask(t));

  /** A task's TOTAL effort in ideal days (own custom effort or subtask sum). */
  const eff = (t: Task): number =>
    totalEffortDays(t, subtasksByParent?.get(t.id));

  // Group by owner; collect the unassigned pool.
  const groups = new Map<string, Task[]>();
  const unassigned: Task[] = [];
  for (const t of topLevel) {
    const owner = (t.owner ?? '').trim();
    if (!owner) {
      unassigned.push(t);
      continue;
    }
    const arr = groups.get(owner);
    if (arr) arr.push(t);
    else groups.set(owner, [t]);
  }

  // Reuse the capacity engine for the member breakdown (occupied/bandwidth/over).
  const capacityMembers: CapacityMember[] = (members ?? []).map((m) => ({
    alias: m.alias,
    oncallDays: memberOncallDays(oncallDaysByAlias, m.alias, sprintWorkingDays),
  }));
  const capacityTasks = topLevel.map((t) => ({ owner: t.owner, effortDays: eff(t) }));
  const loads = engineerLoads(
    capacityMembers,
    sprintWorkingDays,
    capacityTasks,
    commonReductionDays,
  );
  const loadByAlias = new Map(loads.map((l) => [l.alias, l]));
  const memberByAlias = new Map(
    (members ?? []).map((m) => [m.alias.trim().toLowerCase(), m]),
  );

  const lanes: PersonLane[] = [];
  for (const [alias, laneTasks] of groups) {
    const load = loadByAlias.get(alias);
    const assignedDays = load
      ? load.assignedDays
      : round2(laneTasks.reduce((sum, t) => sum + eff(t), 0));
    // Non-roster owners still get a real bandwidth figure for the sprint.
    const capacityDays = load
      ? load.capacityDays
      : memberBandwidthDays(sprintWorkingDays, 0, commonReductionDays);
    const member = memberByAlias.get(alias.toLowerCase());
    lanes.push({
      alias,
      name: member?.name?.trim() || alias,
      role: member?.role?.trim() || '',
      isUnassigned: false,
      tasks: laneTasks,
      assignedDays,
      capacityDays,
      remainingDays: round2(capacityDays - assignedDays),
      overCapacity: assignedDays > capacityDays,
      storyPoints: sumStoryPoints(laneTasks),
      counts: tally(laneTasks),
    });
  }

  lanes.sort(
    (a, b) =>
      b.assignedDays - a.assignedDays || a.name.localeCompare(b.name),
  );

  if (unassigned.length > 0) {
    lanes.push({
      alias: '',
      name: 'Unassigned',
      role: '',
      isUnassigned: true,
      tasks: unassigned,
      assignedDays: round2(unassigned.reduce((sum, t) => sum + eff(t), 0)),
      capacityDays: 0,
      remainingDays: 0,
      overCapacity: false,
      storyPoints: sumStoryPoints(unassigned),
      counts: tally(unassigned),
    });
  }

  return lanes;
}
